import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, AlertCircle, ShieldAlert } from 'lucide-react';
import MassMasterLogo from '../components/MassMasterLogo';

// Registration lock - matches backend setting
const REGISTRATION_LOCKED = true;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('Attempting login with:', email);
      const result = await login(email, password);
      console.log('Login successful:', result);
      navigate('/app');
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <MassMasterLogo size="md" />
          </div>
          <CardTitle className="text-2xl">Mass Master Pro</CardTitle>
          <CardDescription>
            Sign in to access the calculator
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Admin Access Only Notice */}
          {REGISTRATION_LOCKED && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Admin Access Only</p>
                <p className="text-xs text-amber-700 mt-1">
                  This application is currently in private beta. Only authorized admin accounts can access.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@massmasterpro.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={6}
                />
              </div>
            </div>
            
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Please wait...' : 'Sign In'}
            </Button>
          </form>
          
          <p className="text-center text-xs text-muted-foreground mt-6">
            Contact administrator for access
          </p>
        </CardContent>
      </Card>
      
      {/* Fine print disclaimer at bottom */}
      <p className="text-[10px] text-muted-foreground/70 mt-8 max-w-md text-center leading-relaxed">
        By using Mass Master Pro™, you acknowledge that all calculations are estimates for informational purposes only. 
        Mass Master Pro and its affiliates are not liable for any errors, omissions, or decisions made based on this application. 
        Users are solely responsible for verifying all calculations. © {new Date().getFullYear()} Mass Master Pro™. All Rights Reserved.
      </p>
    </div>
  );
};

export default LoginPage;
